import React, { useCallback, useRef } from 'react';
import useIsTopOrEndReached from '@/src/hooks/useIsTopOrEndReached';
import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import Image from 'next/image';
import classNames from 'classnames';
import { AreaOptions, LocationData } from '@/swagger';
import { isTrue } from '@/utils/formatChecker';
import clsx from 'clsx';
import ScrollableMaskContainer from '@/components/common/ScrollableMaskContainer';

const CityOptions: React.FC<{ data: Array<string>; cities: Array<string>; onCitySelect: (city: string) => void }> = ({
  data,
  cities,
  onCitySelect,
}) => {
  const boxRef = useRef<HTMLDivElement | null>(null);

  const { onContainerScroll, bottomEndReached, topEndReached } = useIsTopOrEndReached({ container: boxRef.current });

  return (
    <React.Fragment>
      <Box
        ref={boxRef}
        onScroll={onContainerScroll}
        id={'city-option-container'}
        w={'full'}
        display={'flex'}
        flexWrap={{ md: 'wrap' }}
        flexDirection={{ base: 'column', md: 'row' }}
        className={'relative overflow-scroll'}
      >
        {data.map((city) => {
          const isSelected = cities.includes(city);

          return (
            <Button
              p={{ base: 0 }}
              py={{ md: 1, base: 4 }}
              pr={{ md: 4 }}
              height={'auto'}
              bgColor={'transparent'}
              key={city}
              onClick={() => {
                onCitySelect(city);
              }}
            >
              <Flex
                align={'center'}
                justify={{ md: 'center', base: 'space-between' }}
                flexDirection={{ md: 'row', base: 'row-reverse' }}
                w={'full'}
                borderColor={isSelected ? 'black' : 'gray.500'}
              >
                <Stack
                  border={'2px solid black'}
                  position={'relative'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  mr={{ md: 2 }}
                  width={18}
                  height={18}
                  borderRadius={4}
                >
                  <ConditionalFragment condition={isSelected}>
                    <Image src={'/check-icon.svg'} layout={'fill'} />
                  </ConditionalFragment>
                </Stack>
                <Text fontSize={16} lineHeight={'24px'} letterSpacing={0.5} fontWeight={'400'} textColor={'black'}>
                  {city}
                </Text>
              </Flex>
            </Button>
          );
        })}
      </Box>
      <div
        className={classNames('md:invisible md:hidden absolute top-0 left-0 w-full h-16 pointer-events-none', {
          'block visible': !topEndReached,
          'hidden invisible': topEndReached,
        })}
        style={{
          backgroundImage: 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.4) 100%)',
        }}
      />
      <div
        className={classNames('md:invisible md:hidden absolute bottom-0 left-0 w-full h-16 pointer-events-none', {
          'block visible': !bottomEndReached,
          'hidden invisible': bottomEndReached,
        })}
        style={{
          backgroundImage: 'linear-gradient(to top, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.4) 100%)',
        }}
      />
    </React.Fragment>
  );
};

export default CityOptions;

export const Checkbox: React.FC<{
  checked: boolean | 'indeterminate';
}> = ({ checked }) => {
  return (
    <Stack
      border={'2px solid black'}
      position={'relative'}
      alignItems={'center'}
      justifyContent={'center'}
      width={18}
      height={18}
      borderRadius={4}
    >
      <ConditionalFragment condition={isTrue(checked)}>
        <Image src={'/check-icon.svg'} layout={'fill'} />
      </ConditionalFragment>
      <ConditionalFragment condition={checked === 'indeterminate'}>
        <Flex align={'center'} justify={'center'} w={'full'} h={'full'} bg={'gray.900'} p={1}>
          <div className={clsx('w-full h-0.5', 'bg-white')} />
        </Flex>
      </ConditionalFragment>
    </Stack>
  );
};

export const CitySectionOptions: React.FC<{
  data: Array<AreaOptions>;
  cities: Array<string>;
  onCitySelect: (city: string) => void;
  location: Array<LocationData> | undefined;
  setFilteredCities?: React.Dispatch<React.SetStateAction<Array<string>>>;
}> = ({ data, cities, onCitySelect, location = [], setFilteredCities }) => {
  const getStateTitle = useCallback(
    (key: string) => {
      const usLocation = location;
      if (!usLocation) return '';
      const target = usLocation.find((state) => state.key === key);
      if (!target) return '';
      return `${target.zh_tw} ${target.en}`;
    },
    [location]
  );

  const onCityClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const city = e.currentTarget.dataset.city;
      if (!city) return;
      onCitySelect(city);
    },
    [onCitySelect]
  );

  const checkStateAllSelected = useCallback(
    (state: string) => {
      const targetState = data.find((s) => s.state === state);
      if (!targetState) return false;
      let checked: boolean | 'indeterminate' = targetState.cities.every((city) => cities.includes(city));
      if (!checked && targetState.cities.some((city) => cities.includes(city))) {
        checked = 'indeterminate';
      }
      return checked;
    },
    [data, cities]
  );

  const onStateClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const state = e.currentTarget.dataset.state;
      const targetState = data.find((s) => s.state === state);
      if (!targetState) return;
      const isAllSelected = checkStateAllSelected(state!);
      if (isTrue(isAllSelected)) {
        setFilteredCities?.((prev) => prev.filter((city) => !targetState.cities.includes(city)));
      } else {
        setFilteredCities?.((prev) => [...new Set([...prev, ...targetState.cities])]);
      }
    },
    [data, checkStateAllSelected, setFilteredCities]
  );

  if (!location) return null;

  return (
    <div className={'-mx-4 h-full'}>
      <ScrollableMaskContainer className={'p-4 md:py-0'} direction={'vertical'}>
        {data.map((state) => {
          return (
            <div key={state.state}>
              <Flex
                as={'button'}
                data-state={state.state}
                alignItems={'center'}
                py={{ md: 1, base: 3 }}
                experimental_spaceX={2}
                // @ts-ignore
                onClick={onStateClick}
                w={'full'}
                flexDirection={{ md: 'row-reverse' }}
              >
                <p
                  className={clsx(
                    'text-left',
                    'flex-1 text-lg md:text-base font-bold text-gray900 underline md:no-underline'
                  )}
                >
                  {getStateTitle(state.state)}
                </p>
                <Checkbox checked={checkStateAllSelected(state.state)} />
              </Flex>
              <Box
                id={'city-option-container'}
                w={'full'}
                display={'flex'}
                flexWrap={{ md: 'wrap' }}
                flexDirection={{ base: 'column', md: 'row' }}
                className={'relative overflow-scroll'}
              >
                {state.cities.map((city) => {
                  const isSelected = cities.includes(city);

                  return (
                    <Button
                      p={{ base: 0 }}
                      py={{ md: 1, base: 4 }}
                      pr={{ md: 4 }}
                      height={'auto'}
                      bgColor={'transparent'}
                      key={city}
                      onClick={onCityClick}
                      data-city={city}
                    >
                      <Flex
                        align={'center'}
                        justify={{ md: 'center', base: 'space-between' }}
                        flexDirection={{ md: 'row', base: 'row-reverse' }}
                        w={'full'}
                        borderColor={isSelected ? 'black' : 'gray.500'}
                        className={'space-x-2'}
                      >
                        <Checkbox checked={isSelected} />
                        <Text
                          fontSize={16}
                          lineHeight={'24px'}
                          letterSpacing={0.5}
                          fontWeight={'400'}
                          textColor={'black'}
                        >
                          {city}
                        </Text>
                      </Flex>
                    </Button>
                  );
                })}
              </Box>
            </div>
          );
        })}
      </ScrollableMaskContainer>
    </div>
  );
};
